/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import PublicFamilyMemberForm from './PublicFamilyMemberForm';
import AddNewGridButton from '../../../../commonComponents/AddNewGridButton';
import EditDeleteCommandCell from '../../../../commonComponents/gridCells/EditDeleteCommandCell';
import FullNameCell from '../../../../commonComponents/gridCells/FullNameCell';
import MobileSummaryCell from '../../../../commonComponents/gridCells/MobileSummaryCell';
import useAsyncAction, { useWindowDimensions } from '../../../../commonComponents/Hooks';
import PrimaryButton from '../../../../commonComponents/PrimaryButton';
import SecondaryButton from '../../../../commonComponents/SecondaryButton';
import { deleteClientFamilyMember, getClientFamilyMembers } from '../../../../core/api/publicClientApi';
import { updateVisibleColumns } from '../../../../core/domain';
import { GridCellTypes } from '../../../../core/enums';
import ColumnInterface from '../../../../models/ColumnInterface';
import { createDefaultFamilyMember, PublicClientFamily } from '../../../../models/PublicClientModels';
import { isPhoneScreen, twoIconsCellWidth } from '../../../cssConstants';
import { Paragraph, PromptButtonsWrapper } from '../../../Styles';
import PublicFormButtons from '../common/PublicFormButtons';
import Prompt from '../../../common/Prompt';
import { PublicStyledGrid, StyledGridContainer, SubTitleParagraph, TitleParagraph } from '../common/PublicLayout';
import { useProgress } from '../common/PublicProgressContext';

const PublicFamilyMembersGrid = (props: Props) => {
	const { onBackButtonClick, onContinueButtonClick } = props;
	const { width: screenWidth } = useWindowDimensions();
	const [columns, setColumns] = React.useState(initialColumns);
	const [familyMembers, setFamilyMembers] = React.useState<PublicClientFamily[]>([]);
	const [openForm, setOpenForm] = React.useState(false);
	const [editItem, setEditItem] = React.useState<PublicClientFamily>();
	const [deleteItem, setDeleteItem] = React.useState<PublicClientFamily>();
	const { clientBasicInfoCompleted } = useProgress();

	const performAsyncAction = useAsyncAction();
	const { signal } = new AbortController();

	React.useEffect(() => {
		if (clientBasicInfoCompleted) {
			performAsyncAction(async () => {
				const existingFamilyMembers = await getClientFamilyMembers(signal);
				setFamilyMembers(existingFamilyMembers);
			});
		}
	}, []);

	React.useEffect(() => {
		updateVisibleColumns(initialColumns, columns, screenWidth, setColumns);
	}, [screenWidth]);

	const handleAddNew = () => {
		setEditItem(createDefaultFamilyMember());
		setOpenForm(true);
	};

	const handleEdit = (item: PublicClientFamily) => {
		setEditItem(createDefaultFamilyMember(familyMembers.find((x) => x.clientId === item.clientId)));
		setOpenForm(true);
	};

	const handleCancelEdit = () => {
		setOpenForm(false);
	};

	const handleSubmit = (newFamilyMember: PublicClientFamily) => {
		let newData = familyMembers;
		if (familyMembers.find((fm: PublicClientFamily) => fm.clientId === newFamilyMember.clientId)) {
			newData = familyMembers.map((item) => {
				if (newFamilyMember.clientId === item.clientId) {
					// eslint-disable-next-line no-param-reassign
					item = { ...newFamilyMember };
				}
				return item;
			});
		} else {
			newData = [...familyMembers, newFamilyMember];
		}
		setFamilyMembers(newData);
		setOpenForm(false);
	};

	const openConfirmDeleteDialog = (item: PublicClientFamily) => {
		setDeleteItem(item);
	};

	const closeConfirmDeleteDialog = () => {
		setDeleteItem(null);
	};

	const handleDelete = async () => {
		await performAsyncAction(async () => {
			await deleteClientFamilyMember(deleteItem.clientId, signal);
			closeConfirmDeleteDialog();
		});
		const newData = familyMembers.filter((item) => deleteItem.clientId !== item.clientId);
		setFamilyMembers(newData);
	};

	return (
		<>
			{!openForm && (
				<>
					<TitleParagraph>Family Members</TitleParagraph>
					<SubTitleParagraph>
						Would you like to include family members on your account? If not, click continue.
					</SubTitleParagraph>
					<StyledGridContainer>
						<PublicStyledGrid data={familyMembers} key={columns.length}>
							{columns.map((column) => {
								if (column.cellType === GridCellTypes.FullName)
									return <GridColumn {...column} cells={{ data: FullNameCell }} key={column.field} />;
								return <GridColumn {...column} key={column.field} />;
							})}
							{isPhoneScreen(screenWidth) && <GridColumn cells={{ data: MobileSummaryCell(initialColumns) }} />}
							<GridColumn
								cells={{ data: EditDeleteCommandCell(handleEdit, openConfirmDeleteDialog) }}
								width={twoIconsCellWidth(screenWidth)}
							/>
						</PublicStyledGrid>
						<GridToolbar>
							<AddNewGridButton fullName="Add a family member" onClick={handleAddNew} />
						</GridToolbar>
					</StyledGridContainer>
					{deleteItem && (
						<Prompt
							title="Delete Family Member"
							content={
								<Paragraph>{`Do you want to delete this family member: ${deleteItem.firstName} ${deleteItem.lastName}?`}</Paragraph>
							}
							onClose={closeConfirmDeleteDialog}
							actions={
								<PromptButtonsWrapper>
									<SecondaryButton text="Cancel" onClick={closeConfirmDeleteDialog} />
									<PrimaryButton text="Delete" fullWidth={false} onClick={async () => handleDelete()} />
								</PromptButtonsWrapper>
							}
						/>
					)}
					<PublicFormButtons onBackButtonClick={onBackButtonClick} onContinueButtonClick={onContinueButtonClick} />
				</>
			)}
			{openForm && (
				<>
					<TitleParagraph>Add a Family Member</TitleParagraph>
					<SubTitleParagraph>Please provide the details of your family member.</SubTitleParagraph>
					<PublicFamilyMemberForm onSubmit={handleSubmit} onCancel={handleCancelEdit} familyMember={editItem} />
				</>
			)}
		</>
	);
};

export default PublicFamilyMembersGrid;

interface Props {
	onBackButtonClick?: () => void;
	onContinueButtonClick?: () => void;
}

const initialColumns: ColumnInterface[] = [
	{ field: 'fullName', title: 'Name', cellType: GridCellTypes.FullName },
	{ field: 'relationship', title: 'Relationship' }
];
